import * as React from "react";
import { useLazyLoadQuery } from "react-relay";
import { useNavigate } from "react-router-dom";
import graphql from "babel-plugin-relay/macro";
import styled from "styled-components";

import { AuthContext } from "../pages/Auth";
import { Button } from "./Buttons/Button";
import { ReactComponent as CardsIcon } from "../assets/cards.svg";
import Spinner from "./Spinner";
import type { SidebarQuery as SidebarQueryType } from "./__generated__/SidebarQuery.graphql";
import UserProfile from "./UserProfile";

// import UserProfile from "./UserProfile";
const SidebarQuery = graphql`
  query SidebarQuery {
    user {
      id
      ...UserProfileFragment
    }
  }
`;

function useOutsideClickAlerter(
  ref: React.RefObject<HTMLDivElement>,
  setExpandProfile: React.Dispatch<React.SetStateAction<boolean>>
) {
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setExpandProfile(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, setExpandProfile]);
}

function UserSection() {
  const authstate = React.useContext(AuthContext);
  const [expandProfile, setExpandProfile] = React.useState(false);
  const profilePage = authstate?.active_features?.includes("ACCOUNT_MIGRATION");
  const reference = React.useRef<HTMLDivElement>(null);
  useOutsideClickAlerter(reference, setExpandProfile);

  const data = useLazyLoadQuery<SidebarQueryType>(SidebarQuery, {});

  return profilePage ? (
    <div ref={reference} style={{ position: "relative" }}>
      <Section onClick={() => setExpandProfile(!expandProfile)}>
        {data?.user && <UserProfile user={data.user} />}
      </Section>
      {expandProfile && (
        <ExpandedProfile parentHeight={reference.current?.clientHeight || 0}>
          <Button
            label="Mijn gegevens"
            color="white"
            icon="carret-right"
            iconSize="large"
            iconSide="right"
            onClick={() => {
              if (authstate?.profileUrl) {
                window.open(authstate.profileUrl, "_blank");
              }
            }}
          />
          <Divider />
          <Button
            label="Wachtwoord"
            color="white"
            icon="carret-right"
            iconSize="large"
            iconSide="right"
            onClick={() => {
              if (authstate?.profileUrl) {
                window.open(`${authstate.profileUrl}#password`, "_blank");
              }
            }}
          />
          <Divider />
          <Button
            label="Uitloggen"
            color="white"
            icon="exit"
            onClick={() => {
              const confirmed = window.confirm("Uitloggen?");
              if (confirmed) {
                window.sessionStorage.clear();
                window.location.href = "/o/logout/";
              }
            }}
          />
        </ExpandedProfile>
      )}
    </div>
  ) : (
    <Section
      onClick={() => {
        window.location.href = "/account";
      }}
    >
      {data?.user && <UserProfile user={data.user} />}
    </Section>
  );
}

export default function Sidebar() {
  const navigate = useNavigate();
  return (
    <Container>
      <React.Suspense
        fallback={
          <Section>
            <Spinner circleColor="white" indicatorColor="rgb(0, 117, 188)" />
          </Section>
        }
      >
        <UserSection />
      </React.Suspense>
      <Section>
        <SidebarButton onClick={() => navigate("/")}>
          <CardsIcon fill="#212529" width={17} height={14} />
          <SidebarButtonText>Schouw</SidebarButtonText>
        </SidebarButton>
      </Section>
      <Section />
    </Container>
  );
}

const Container = styled.div`
  margin: 27px;
  transition: all 0.5s ease;

  background: #0075bc;
  box-shadow: 0px 5px 25px -5px #0075bc;
  border-radius: 47.5px;

  display: flex;
  flex-direction: column;
  justify-content: space-between;

  padding: 13px;
  padding-top: 25px;
  padding-bottom: 25px;

  @media only screen and (max-width: 768px) {
    display: none;
  }
`;

const SidebarButton = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;

  width: 68px;
  height: 113px;
  background: #ffffff;
  border-radius: 50px;
`;
const SidebarButtonText = styled.div`
  padding-top: 11px;
  /* Tablet/Label bold */
  font-family: "Roboto";
  font-style: normal;
  font-weight: 700;
  font-size: 13px;
  line-height: 120%;

  color: #212529;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

const ExpandedProfile = styled.div<{ parentHeight: number }>`
  position: absolute;
  background: white;
  top: ${(props) => props.parentHeight + 8}px;
  border-radius: 5px;
  border: 1px solid #e5e5e5;
  left: 0;
  width: 200px;
  z-index: 1;
  padding: 4px;
  gap: 4px;
  display: flex;
  flex-direction: column;
`;

const Divider = styled.div`
  width: 100%;
  border-top: 1px solid #f7f7f7;
`;
